import { X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { motionOn } from '../lib/motion.ts'
import { lockScroll } from '../lib/scroll.ts'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

const LEAVE_MS = 180

/**
 * Bottom sheet (modal dialog on desktop): closes on backdrop, Esc and the close button; focus moves in, is trapped,
 * and returns to the opener on close (docs/UX-PLAYBOOK.md §5, §11). It slides down before it goes (§8), and the page
 * behind it does not scroll meanwhile.
 */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const [leaving, setLeaving] = useState(false)
  const close = useRef(onClose)
  close.current = onClose
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const requestClose = useRef(() => {})
  requestClose.current = () => {
    if (!motionOn()) return close.current()
    if (timer.current) return
    setLeaving(true)
    timer.current = setTimeout(() => close.current(), LEAVE_MS)
  }

  useEffect(() => lockScroll(), [])
  useEffect(() => () => clearTimeout(timer.current), [])

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); requestClose.current() }
      if (e.key !== 'Tab' || !ref.current) return
      const items = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); opener?.focus?.() }
  }, [])

  return (
    <div className={`sheet-backdrop${leaving ? ' closing' : ''}`} onClick={() => requestClose.current()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} tabIndex={-1} data-lenis-prevent onClick={(e) => e.stopPropagation()}>
        <div className="grabber" aria-hidden="true" />
        <div className="sheet-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn plain" onClick={() => requestClose.current()} aria-label="Tutup"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}
